/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
*/
import {batch} from 'react-redux';
import {isEmpty as isEmptyObj} from 'lodash';
import isEqual from 'lodash.isequal';
import isString from 'lodash.isstring';
import isUndefined from 'lodash.isundefined';
import pickBy from 'lodash.pickby';
import {getAccountId} from '@ion/applications/src/helpers';
import {
  errorAddOptions,
  errorCloneOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '@ion/applications/src/prompts/constants';
import {toggleUserPrompt} from '@ion/applications/src/store/actions/user-prompt';
import {error} from '@ion/core/src/logger/logger';
import {
  clonePEGBehavior as pegBehaviorClone,
  createPegBehavior,
  createPEGEvents,
  deletePegAccumulator,
  destroyPegBehavior,
  getPegBehavior,
  getPegBehaviors,
  getPegEvent,
  getPegLabels,
  updatePegBehavior,
  updatePEGEvents
} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  AuditChange,
  ClonedPegBehavior,
  Entity,
  GenericObject,
  PegAccumulators,
  PegBehavior,
  PegBehaviorsFilters,
  PegEvent,
  PegEvents,
  PegLabels,
  PegLabelType
} from '@ion/core/src/types/entities';
import {
  LoadPegBehaviorsParams,
  LoadPegLabelsParams
} from '@ion/core/src/types/services';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import {retryUpdate} from '@ion/services/src/helpers/retry';
import {PB_STEPS} from '@/routes/admin/pegbehaviors/PegBehaviorsEditorUtils';
import {getDeletedAccumulators} from '@/routes/admin/pegbehaviors/PegConfigUtils';
import {getAccumulatorsByUniqId} from '@/routes/admin/pegbehaviors/PegEventAccumulatorUtils';
import {PageInitializeAction} from '@/types/redux';

// Defines the name of the page Behavior's reducer
export const REDUCER_NAME = 'pegBehaviors';

export const INITIALIZE_ADMIN_PEG_BEHAVIORS          = 'INITIALIZE_ADMIN_PEG_BEHAVIORS';
export const SET_ADMIN_PEG_BEHAVIORS_GRID_COLUMNS    = 'SET_ADMIN_PEG_BEHAVIORS_GRID_COLUMNS';
export const UPDATE_ADMIN_PEG_BEHAVIORS_GRID_COLUMNS = 'UPDATE_ADMIN_PEG_BEHAVIORS_GRID_COLUMNS';
export const ADMIN_PEG_BEHAVIORS_GRID_REFRESHED      = 'ADMIN_PEG_BEHAVIORS_GRID_REFRESHED';
export const SET_GRID_PAGE_SIZE                      = 'SET_PEG_BEHAVIORS_GRID_PAGE_SIZE';
export const GET_ADMIN_PEG_BEHAVIORS_REQUEST         = 'GET_ADMIN_PEG_BEHAVIORS_REQUEST';
export const GET_ADMIN_PEG_BEHAVIORS_RESPONSE        = 'GET_ADMIN_PEG_BEHAVIORS_RESPONSE';
export const GET_ADMIN_PEG_BEHAVIORS_ERROR           = 'GET_ADMIN_PEG_BEHAVIORS_ERROR';

export const GET_ADMIN_PB_PEG_LABELS_REQUEST         = 'GET_ADMIN_PB_PEG_LABELS_REQUEST';
export const GET_ADMIN_PB_PEG_LABELS_RESPONSE        = 'GET_ADMIN_PB_PEG_LABELS_RESPONSE';
export const GET_ADMIN_PB_PEG_LABELS_ERROR           = 'GET_ADMIN_PB_PEG_LABELS_ERROR';

export const DELETE_PEG_BEHAVIOR                    = 'DELETE_PEG_BEHAVIOR';
export const DELETE_ADMIN_PEG_BEHAVIOR_REQUEST      = 'DELETE_ADMIN_PEG_BEHAVIOR_REQUEST';
export const DELETE_ADMIN_PEG_BEHAVIOR_RESPONSE     = 'DELETE_ADMIN_PEG_BEHAVIOR_RESPONSE';
export const DELETE_ADMIN_PEG_BEHAVIOR_ERROR        = 'DELETE_ADMIN_PEG_BEHAVIOR_ERROR';

export const CLONE_PEG_BEHAVIOR                    = 'CLONE_PEG_BEHAVIOR';
export const CLONE_ADMIN_PEG_BEHAVIOR_REQUEST      = 'CLONE_ADMIN_PEG_BEHAVIOR_REQUEST';
export const CLONE_ADMIN_PEG_BEHAVIOR_RESPONSE     = 'CLONE_ADMIN_PEG_BEHAVIOR_RESPONSE';
export const CLONE_ADMIN_PEG_BEHAVIOR_ERROR        = 'CLONE_ADMIN_PEG_BEHAVIOR_ERROR';

// search queries
export const UPDATE_ADMIN_PEG_BEHAVIORS_SEARCH_QUERIES = 'UPDATE_ADMIN_PEG_BEHAVIORS_SEARCH_QUERIES';
export const RESET_ADMIN_PEG_BEHAVIORS_SEARCH_QUERIES  = 'RESET_ADMIN_PEG_BEHAVIORS_SEARCH_QUERIES';

// Add PEG Behavior
export const SHOW_ADD_PEG_BEHAVIOR_DIALOG            = 'SHOW_ADD_PEG_BEHAVIOR_DIALOG';
export const ADD_ADMIN_PEG_BEHAVIOR_REQUEST          = 'ADD_ADMIN_PEG_BEHAVIOR_REQUEST';
export const ADD_ADMIN_PEG_BEHAVIOR_RESPONSE         = 'ADD_ADMIN_PEG_BEHAVIOR_RESPONSE';
export const ADD_ADMIN_PEG_BEHAVIOR_ERROR            = 'ADD_ADMIN_PEG_BEHAVIOR_ERROR';

//Edit PEG Behavior
export const SHOW_EDIT_PEG_BEHAVIOR_DIALOG           = 'SHOW_EDIT_PEG_BEHAVIOR_DIALOG';
export const EDIT_ADMIN_PEG_BEHAVIOR_REQUEST         = 'EDIT_ADMIN_PEG_BEHAVIOR_REQUEST';
export const EDIT_ADMIN_PEG_BEHAVIOR_RESPONSE        = 'EDIT_ADMIN_PEG_BEHAVIOR_RESPONSE';
export const EDIT_ADMIN_PEG_BEHAVIOR_ERROR           = 'EDIT_ADMIN_PEG_BEHAVIOR_ERROR';

//View PEG Behavior
export const SHOW_VIEW_PEG_BEHAVIOR_DIALOG           = 'SHOW_VIEW_PEG_BEHAVIOR_DIALOG';
export const VIEW_ADMIN_PEG_BEHAVIOR_REQUEST         = 'VIEW_ADMIN_PEG_BEHAVIOR_REQUEST';
export const VIEW_ADMIN_PEG_BEHAVIOR_RESPONSE        = 'VIEW_ADMIN_PEG_BEHAVIOR_RESPONSE';
export const VIEW_ADMIN_PEG_BEHAVIOR_ERROR           = 'VIEW_ADMIN_PEG_BEHAVIOR_ERROR';

// Audit PEG Behavior
export const AUDIT_PEG_BEHAVIOR                      = 'AUDIT_PEG_BEHAVIOR';
export const AUDIT_PEG_BEHAVIOR_SHOW_CHANGES_DIALOG  = 'AUDIT_PEG_BEHAVIOR_SHOW_CHANGES_DIALOG';

/**
 * Kicks off any redux initialization for PEG Behaviors
 */
export const initializeAdminPegBehaviors = (): PageInitializeAction => ({
  type : INITIALIZE_ADMIN_PEG_BEHAVIORS
});

// Dispatches an action when the Peg Behaviors grid has been refreshed
export const adminPegBehaviorsGridRefreshed = (): PageInitializeAction => ({
  type : ADMIN_PEG_BEHAVIORS_GRID_REFRESHED
});

// Updates the modular search queries for PEG Behaviors
export const updatePegBehaviorsSearchQueries = (searchParams: PegBehaviorsFilters) => ({
  type : UPDATE_ADMIN_PEG_BEHAVIORS_SEARCH_QUERIES, searchParams
});

// Resets the PEG Behaviors filters state
export const resetPegBehaviorsSearchQueries = () => ({
  type : RESET_ADMIN_PEG_BEHAVIORS_SEARCH_QUERIES
});

/**
 * Invoked when the the PEG Behaviors auditor is to be displayed or removed.
 */
export const auditPegBehaviors = (pegBehavior: PegBehavior, show = true) => ({
  type : AUDIT_PEG_BEHAVIOR, pegBehavior, show
});

export const showAuditPegBehaviorsChangesDialog = (auditRecord: AuditChange, show = true) => ({
  type : AUDIT_PEG_BEHAVIOR_SHOW_CHANGES_DIALOG, auditRecord, show
});

/**
 * Retrive all PEG labels with type EVENT and ACCUMULATOR.
 */
export const getAdminPbPegLabels = ({
  accountId,
  step = PB_STEPS.EVENT_CODE,
  filterOn = {}
}) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: GET_ADMIN_PB_PEG_LABELS_REQUEST, isSearch: !isEmptyObj(filterOn)});
    try {
      const endpointOptions: LoadPegLabelsParams = {
        isLoadAll : true,
        type      : step === PB_STEPS.ACCUMULATORS ? PegLabelType.ACCUMULATOR : PegLabelType.EVENT,
        filterOn,
        accountId
      };
      const {pegLabels} = await getPegLabels(endpointOptions);
      dispatch({type: GET_ADMIN_PB_PEG_LABELS_RESPONSE, allPegLabels: pegLabels});
    } catch (err) {
      dispatch({type: GET_ADMIN_PB_PEG_LABELS_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Retrieves PEG Behaviors from services.
 */
export const getAdminPegBehaviors = ({
  sort,
  callback,
  pageId = 1,
  pageSize = 20
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_ADMIN_PEG_BEHAVIORS_REQUEST});
    const {
      pegBehaviors: {
        search
      }
    } = getState();
    const endpointOptions: LoadPegBehaviorsParams = {
      accountId : getAccountId(getState),
      pageId,
      pageSize
    };
    if (sort) {
      endpointOptions.sortOn = sort;
    }
    const availableStringSearchAttributes = pickBy(search, isString);
    endpointOptions.filterOn = availableStringSearchAttributes;
    try {
      const {pegBehaviors: items, totalCount} = await getPegBehaviors(endpointOptions);
      dispatch({type: GET_ADMIN_PEG_BEHAVIORS_RESPONSE});
      callback?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      callback({error: err, items: [], totalCount: 0});
      dispatch({type: GET_ADMIN_PEG_BEHAVIORS_ERROR, error: err});
      return err;
    }
  };
};

// Dispatches an action to show or hide the Peg Behavior deletion confirmation dialog to the user
export const displayDeleteDialog = (pegBehavior: PegBehavior, show = true) => ({
  type : DELETE_PEG_BEHAVIOR, pegBehavior, show
});

// Dispatches actions related to deleting a Peg Behavior
export const deleteAdminPegBehavior = (PegBehavior: PegBehavior) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_PEG_BEHAVIOR_REQUEST});
    try {
      const deleted = await destroyPegBehavior(PegBehavior);
      dispatch({type: DELETE_ADMIN_PEG_BEHAVIOR_RESPONSE, pegBehavior: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_PEG_BEHAVIOR_ERROR, error: err});
      error(err);
    }
  };
};

// Dispatches an action to show or hide the Peg Behavior deletion confirmation dialog to the user
export const displayCloneDialog = (pegBehavior: ClonedPegBehavior, show = true) => ({
  type : CLONE_PEG_BEHAVIOR, pegBehavior, show
});

// Dispatches actions related to deleting a Peg Behavior
export const clonePegBehavior = (clonePegBehavior: ClonedPegBehavior) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: CLONE_ADMIN_PEG_BEHAVIOR_REQUEST});
    try {
      const {name, destination, inheritable, description, id} = clonePegBehavior;
      const clone = {
        name        : name,
        destination : destination,
        inheritable : inheritable,
        description : description
      };
      const cloned = await pegBehaviorClone(id, clone);
      dispatch({type: CLONE_ADMIN_PEG_BEHAVIOR_RESPONSE, pegBehavior: cloned});
      dispatch(toggleUserPrompt(successAddOptions(Entity.pegbehavior, true)));
    } catch (err) {
      dispatch({type: CLONE_ADMIN_PEG_BEHAVIOR_ERROR, error: err});
      dispatch(toggleUserPrompt(errorCloneOptions(Entity.pegbehavior, err, false)));
      error(err);
    }
  };
};

// Opens or closes the Add Peg Label dialog
export const showAddPegBehaviorDialog = (showDialog: boolean = true) => ({
  type : SHOW_ADD_PEG_BEHAVIOR_DIALOG, showDialog
});

// Dispatches actions related to creating the given Peg Label
export const addPegBehavior = (pegBehavior: PegBehavior) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: ADD_ADMIN_PEG_BEHAVIOR_REQUEST});
    const pegEvents             = normalizePEGEvents(pegBehavior.pegEvents);
    const normalizedPegbehavior = normalizePEGBehavior(pegBehavior);
    try {
      const pegBehaviorCreated = await createPegBehavior(normalizedPegbehavior);
      const pegEventsCreated   = await createPEGEvents(pegBehaviorCreated, pegEvents);
      if (pegEventsCreated) {
        pegBehaviorCreated.pegEvents = pegEventsCreated;
      }
      batch(() => {
        dispatch({type: ADD_ADMIN_PEG_BEHAVIOR_RESPONSE, pegBehavior: pegBehaviorCreated});
        dispatch(toggleUserPrompt(successAddOptions(Entity.pegbehavior)));
      });
    } catch (err) {
      batch(() => {
        dispatch({type: ADD_ADMIN_PEG_BEHAVIOR_ERROR, error: err});
        dispatch(toggleUserPrompt(errorAddOptions(Entity.pegbehavior, err)));
      });
      error(err);
    }
  };
};

// Shows or hides the PEG Behavior edit dialog
export const showUpdateStatusPBDialog = (
  pegBehavior: PegBehavior,
  showStatusDialog: boolean = true,
  showDialog: boolean = false
) => ({
  type : SHOW_EDIT_PEG_BEHAVIOR_DIALOG, pegBehavior, showStatusDialog, showDialog
});

// Shows or hides the PEG Behavior edit dialog
export const showEditPBDialog = (pegBehavior: PegBehavior, showDialog: boolean = true, isView: boolean = false) => ({
  type : SHOW_EDIT_PEG_BEHAVIOR_DIALOG, pegBehavior, showDialog, isView
});

// Shows or hides the PEG Behavior edit dialog
export const showViewPBDialog = (pegBehavior: PegBehavior, showDialog: boolean = true) => ({
  type : SHOW_VIEW_PEG_BEHAVIOR_DIALOG, pegBehavior, showDialog
});

// Handles the dispatching of actions related to editing a PEG Label
export const editPegBehavior = ({
  pegBehavior,
  hasEventsAccumulatorsUpdated,
  hasPegAccumulator,
  pegBehaviorsCopy,
  deletedAccums
}) => {
  const isDeletedAccums = deletedAccums?.length > 0;
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_PEG_BEHAVIOR_REQUEST});
    if (isDeletedAccums) {
      deleteExistingAccumulators(pegBehaviorsCopy, deletedAccums);
    }
    const pegEvents             = normalizePEGEvents(pegBehavior.pegEvents, isDeletedAccums);
    const normalizedPegbehavior = normalizePEGBehavior(pegBehavior);
    try {
      const pegBehaviorUpdated = await retryUpdate({
        newEntity     : normalizedPegbehavior,
        updateEntity  : updatePegBehavior,
        getEntity     : getSinglePegBehavior,
        onError       : pegBehaviorUpdateError,
        isPegBehavior : true,
        dispatch
      });
      if (pegBehaviorUpdated && !hasEventsAccumulatorsUpdated) {
        dispatch({type: EDIT_ADMIN_PEG_BEHAVIOR_RESPONSE, pegBehavior: pegBehaviorUpdated});
        dispatch(toggleUserPrompt(successEditOptions(Entity.pegbehavior)));
      } if (pegBehaviorUpdated && hasEventsAccumulatorsUpdated) {
        const pegEventsUpdated = await updatePegEventsWithRetry({
          pegBehaviorUpdated, pegEvents, dispatch, hasPegAccumulator
        });
        if (!isUndefined(pegEventsUpdated)) {
          pegBehaviorUpdated.pegEvents = pegEventsUpdated;
          dispatch({type: EDIT_ADMIN_PEG_BEHAVIOR_RESPONSE, pegBehavior: pegBehaviorUpdated});
          dispatch(toggleUserPrompt(successEditOptions(Entity.pegbehavior)));
        }
      }
    } catch (err) {
      pegBehaviorUpdateError(err, dispatch);
    }
  };
};

/**
 * Invoked when an existing peg events is to be updated; consolidating the process of
 * Peg Event updates into a single transaction. The process for editing a Peg Events is
 * outlined below.
 *
 * 1.) Update the Peg Events.
 * 2.) When peg event receives Resource Out of State error GET latest peg event.
 * 3.) Update the peg event by passing latest peg event details.
 * 4.) Update the provided Peg Events. See updatePEGEvents.
 *
 */
export const updatePegEventsWithRetry = async({
  pegBehaviorUpdated,
  pegEvents,
  dispatch,
  hasPegAccumulator
}) => {
  try {
    return await updatePEGEvents(pegBehaviorUpdated, pegEvents, hasPegAccumulator);
  } catch (err) {
    const isConflict = isEqual(err?.errorCode, 409);
    if (isConflict) {
      for (const index in pegEvents) {
        const {pegEvent}       = pegEvents?.[index] || {};
        const {pegAccumulator} = pegEvent?.pegAccumulators?.[index] || {};
        const {
          updated, error
        } = await getSinglePegEvent(entityId(pegBehaviorUpdated), entityId(pegEvent));
        if (updated) {
          pegEvent.version       = updated?.version;
          pegAccumulator.version = updated?.pegAccumulators?.[index]?.pegAccumulator?.version;
        } else {
          pegBehaviorUpdateError(error, dispatch);
        }
      }
      try {
        return await updatePEGEvents(pegBehaviorUpdated, pegEvents, hasPegAccumulator);
      } catch (error) {
        pegBehaviorUpdateError(error, dispatch);
      }
    } else {
      pegBehaviorUpdateError(err, dispatch);
    }
  }
};

/**
 * Invoked when an existing Peg Event gets resource out of state, delegating to the
 * fetch latest peg event details successful response or error action accordingly.
 */
const getSinglePegEvent = async(pegBehaviorID: number, pegEventID: number) => {
  try {
    const pegEvent = await getPegEvent(pegBehaviorID, pegEventID);
    return {updated: pegEvent, error: ''};
  } catch (err) {
    error(err);
    return {updated: null, error: err};
  }
};

/**
 * Invoked when an existing Peg Behavior gets resource out of state, delegating to the
 * fetch latest peg behavior details successful response or error action accordingly.
 */
const getSinglePegBehavior = async(pegBehavior: PegBehavior) => {
  try {
    const pegBehaviorResult = await getPegBehavior(pegBehavior);
    return {updated: pegBehaviorResult?.[0], error: ''};
  } catch (err) {
    error(err);
    return {updated: null, error: err};
  }
};

/**
 * Invoked when an existing Peg Behavior gets updated or fetched
 * and dispatching a successful or error action accordingly.
 */
const pegBehaviorUpdateError = (err, dispatch: Function) => {
  dispatch({type: EDIT_ADMIN_PEG_BEHAVIOR_ERROR, error: err});
  dispatch(toggleUserPrompt(errorEditOptions(Entity.pegbehavior, err)));
  error(err);
};

// Handles the dispatching of actions related to updating Status of a PEG Behavior
export const updateStatusPegBehavior = (pegBehavior: PegBehavior) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_PEG_BEHAVIOR_REQUEST});
    const normalizedPegbehavior = normalizePEGBehavior(pegBehavior);
    try {
      const pegBehaviorUpdated = await updatePegBehavior(normalizedPegbehavior);
      dispatch({type: EDIT_ADMIN_PEG_BEHAVIOR_RESPONSE, pegBehavior: pegBehaviorUpdated});
      dispatch(toggleUserPrompt(successEditOptions(Entity.pegbehavior)));
    } catch (err) {
      dispatch({type: EDIT_ADMIN_PEG_BEHAVIOR_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(Entity.pegbehavior)));
      error(err);
    }
  };
};

/**
 * Normalize the PEG Behavior properties and passing the empty array for
 * pegEvents to create only PEG Behavior.
 */
export const normalizePEGBehavior = (pegBehavior: PegBehavior) => {
  const {account, description, id, inherited, name, status, version} = pegBehavior;
  return {
    pegEvents : [],
    account,
    id,
    inherited,
    description,
    name,
    status,
    version
  };
};

/**
 * Delete PEG Accumulators on unselection of each cell from PEG configuration
 * grid.
 */
export const destroyPegAccumulators = async(pegAccumulators: PegAccumulators): Promise<void> => {
  const deletedAccumulators = getDeletedAccumulators(pegAccumulators);
  try {
    await Promise.all(deletedAccumulators?.map(async(item) => {
      const {id, pegEvent}    = item.pegAccumulator;
      const [behavior, event] = pegEvent?.href.split('/events/');
      const pegBehaviorId     = parseInt(behavior?.split?.('/')?.reverse()?.[0], 10);
      const pegEventId        = parseInt(event, 10);
      await deletePegAccumulator(pegBehaviorId, pegEventId, id);
    }));
  } catch (err) {
    error(err);
  };
};

/**
 * Normalize the peg accumulators
 */
export const normalizePegAccumulator = (
  pegAccumulators: PegAccumulators,
  pegEventId?: number,
  doNormalize = false
): PegAccumulators => {
  const normalizedAccumulators = [];
  if (!doNormalize) {
    const uniqAccumulators = getAccumulatorsByUniqId(pegAccumulators);
    destroyPegAccumulators(uniqAccumulators);
  }
  pegAccumulators?.forEach((item) => {
    const {
      id = '',
      index,
      units,
      pegLabel,
      pegEvent,
      pegBehavior,
      souQuantity,
      conversionFactor,
      accumulatorFormat,
      isDeleted = false,
      version = 0
    } = item?.pegAccumulator;
    const isTagged   = !isEmpty(pegLabel?.href) && !isEmpty(souQuantity);
    const Identifier = doNormalize ? id : pegEventId && id;
    if (isTagged && !isDeleted) {
      normalizedAccumulators.push({
        pegAccumulator : {
          id : Identifier,
          index,
          units,
          version,
          pegLabel,
          souQuantity,
          conversionFactor,
          accumulatorFormat
        }
      });
    }
  });
  return normalizedAccumulators;
};

/**
 * Normalize the peg events and return the required
 * properties.
 */
export const normalizePEGEvents = (pegEvents: PegEvents, doNormalize = false): PegEvents => {
  pegEvents?.forEach((item: PegEvent) => {
    const {pegAccumulators, id}   = item.pegEvent;
    item.pegEvent.pegAccumulators = normalizePegAccumulator(pegAccumulators, id, doNormalize);
  });
  return pegEvents;
};

export const setPegBehaviorsGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});

/**
 * Delete Existing Accumulators from PEGBehavior Copy and check the modified index
 * and label and delete them out before inserting the new PEG Accumulators.
 */
export const deleteExistingAccumulators = async(pegBehavior: PegBehavior, deletedList: GenericObject) => {
  const {pegEvents, id} = pegBehavior || {};
  try {
    const deletePromises      = pegEvents?.map((item) => {
      const {pegAccumulators} = item?.pegEvent || {};
      return pegAccumulators?.map(async({pegAccumulator}) => {
        const {index, pegLabel} = pegAccumulator || {};
        const indexedLabel      = `${index}-${pegLabel?.title}`;
        if (deletedList?.indexOf(indexedLabel) > -1) {
          return deletePegAccumulator(id, item?.pegEvent?.id, pegAccumulator?.id);
        }
      }).filter(promise => promise !== undefined);
    }).flat();
    await Promise.all(deletePromises);
  } catch (err) {
    error('Error while deleting the accumulators', err);
  }
};
